/**
 * @file useReceiptCommit.ts
 * @description Проведение приёмки с предпроверкой (phase19.22, W20 «срок
 * мягко», W17-9b; блок внутреннего выпуска — решение пользователя 2026-09-26).
 *
 * Два сорта пунктов предпроверки:
 *   - БЛОК — «Внутренний выпуск» без проведённого парного списания «В
 *     производство». Кнопка «Провести» гаснет, над ней красная полоса;
 *     `commit` при блоке не вызывается вовсе. Блок считается сразу по
 *     документу (`localCommitBlocks`) и уточняется `commit-check` сервера;
 *   - предупреждение — срок годности: «Провести без срока / Заполнить сроки».
 * Сбой самой предпроверки проведение не запрещает — сервер всё равно
 * ответит 422 на блок, и его текст станет полосой.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { dedupe, localCommitBlocks, splitCommitCheck, type ReceiptCommitBlock } from "./receipt-commit-block";
import { useWmsConfirm } from "./use-wms-confirm";
import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import { receiptsApi, type GoodsReceiptDto, type ReceiptWarning } from "../../lib/api-wms";
import { wmsErrorText } from "../../lib/wms-error-text";

interface Params {
  receipt: GoodsReceiptDto | null;
  reload: (receiptId: string) => Promise<void>;
  /** Запасной вариант, если перечитать документ после проведения не вышло. */
  setReceipt: (receipt: GoodsReceiptDto) => void;
}

/** Коды 422 проведения, которые показываем полосой-блоком, а не тостом. */
const BLOCK_ERROR_CODES = new Set(["parent_writeoff_required", "parent_writeoff_not_committed"]);

export function useReceiptCommit({ receipt, reload, setReceipt }: Params) {
  const notify = useNotify();
  // Проведение необратимо: второй клик не должен уйти, пока первый в полёте.
  const [committing, setCommitting] = useState(false);
  const [warnings, setWarnings] = useState<ReceiptWarning[] | null>(null);
  const [serverBlocks, setServerBlocks] = useState<ReceiptCommitBlock[]>([]);
  const asker = useWmsConfirm();

  const isDraft = receipt?.status === "DRAFT";
  const blocks = useMemo(
    () => (isDraft ? dedupe([...localCommitBlocks(receipt), ...serverBlocks]) : []),
    [isDraft, receipt, serverBlocks],
  );

  // Документ перечитан (сменили причину, списание, строки) — старый ответ
  // сервера о блоке больше не верен: спрашиваем заново.
  useEffect(() => {
    if (!receipt || receipt.status !== "DRAFT") {
      setServerBlocks([]);
      return;
    }
    let cancelled = false;
    receiptsApi
      .commitCheck(receipt.id)
      .then((res) => {
        if (!cancelled) setServerBlocks(splitCommitCheck(res).blocking);
      })
      .catch(() => {
        if (!cancelled) setServerBlocks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [receipt]);

  const doCommit = useCallback(async () => {
    if (!receipt || blocks.length > 0) return;
    setWarnings(null);
    setCommitting(true);
    try {
      const fresh = await receiptsApi.commit(receipt.id);
      notify.success("Приёмка проведена — можно печатать этикетки");
      try {
        await reload(receipt.id);
      } catch {
        setReceipt(fresh);
      }
    } catch (err) {
      if (err instanceof ApiError && BLOCK_ERROR_CODES.has(err.code)) {
        setServerBlocks([{ code: err.code, message: wmsErrorText(err, err.message) }]);
      } else {
        notify.error(wmsErrorText(err, "Не удалось провести приёмку"));
      }
    } finally {
      setCommitting(false);
    }
  }, [receipt, blocks.length, notify, reload, setReceipt]);

  /** «Провести»: блок — ничего не делаем; иначе предпроверка и подтверждение. */
  const commit = useCallback(async () => {
    if (!receipt || committing || blocks.length > 0) return;
    setCommitting(true);
    let found: ReceiptWarning[] = [];
    try {
      const split = splitCommitCheck(await receiptsApi.commitCheck(receipt.id));
      if (split.blocking.length > 0) {
        setServerBlocks(split.blocking);
        return;
      }
      found = split.warnings;
    } catch {
      // Старый сервер без commit-check или сбой — проводим с обычным вопросом.
      found = [];
    } finally {
      setCommitting(false);
    }
    if (found.length > 0) {
      setWarnings(found);
      return;
    }
    // Окно кабинета вместо системного `confirm()` (phase19.31).
    const ok = await asker.confirm({
      title: "Провести приёмку?",
      description: "После проведения строки документа изменить нельзя.",
      action: "Провести",
    });
    if (!ok) return;
    await doCommit();
  }, [receipt, committing, blocks.length, doCommit, asker]);

  return {
    committing,
    commit,
    /** Вопрос «Провести?» — рисует `ReceiptPageDialogs` (`WmsConfirmDialog`). */
    confirmRequest: asker.request,
    settleConfirm: asker.settle,
    /** Блоки проведения: кнопка неактивна, полоса над ней. */
    blocks,
    /** Предупреждения предпроверки; `null` — диалог закрыт. */
    warnings,
    confirmCommit: doCommit,
    cancel: () => setWarnings(null),
  };
}
